/* ============================================================================
   FRONT LINE DEV - DO NOT EDIT
   This file powers Infinit's custom Quiz and Custom Formula functionality and
   is maintained exclusively by Front Line Dev. Editing it may break the
   formula builder, quiz, saved formulas, or related features. Please contact
   Front Line Dev for any changes.
   ============================================================================ */

/**
 * My Orders page
 *
 * Replaces Shopify's built-in /account orders list for /pages/my-orders/.
 * Fetches normalized orders from /apps/formulas/orders (PHP-backed App
 * Proxy route) and drives "Buy Again" through two paths:
 *
 *   • Custom-formula line items (line.custom_formula === true):
 *       POST /apps/formulas/add-to-cart with `payload_json` rebuilt from
 *       the line's stored properties — same endpoint the saved-formulas
 *       Order modal hits, so pricing and variant creation stay identical.
 *
 *   • Everything else: POST /cart/add.js with the original variant_id +
 *       quantity + line-item properties.
 *
 * After all items are queued the browser is redirected to /cart.
 */
(function () {
  "use strict";

  const root = document.getElementById("my-orders-app");
  if (!root) return;

  const PROXY_BASE = (root.dataset.proxyBase || "/apps/formulas").replace(/\/+$/, "");

  // -----------------------------------------------------------------
  // State
  // -----------------------------------------------------------------
  const state = {
    orders: [],
    loading: false,
    error: "",
    filter: "all",       // all | open | fulfilled | cancelled
    sort: "newest",      // newest | oldest
    buyingOrderId: null, // id of order currently buying-again, for UI lock
  };

  // -----------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------
  function el(tag, attrs, children) {
    const e = document.createElement(tag);
    if (attrs) {
      for (const k in attrs) {
        if (k === "style" && typeof attrs[k] === "object") {
          Object.assign(e.style, attrs[k]);
        } else if (k.startsWith("on") && typeof attrs[k] === "function") {
          e.addEventListener(k.slice(2).toLowerCase(), attrs[k]);
        } else if (k === "class") {
          e.className = attrs[k];
        } else if (k === "html") {
          e.innerHTML = attrs[k];
        } else if (attrs[k] !== null && attrs[k] !== undefined) {
          e.setAttribute(k, attrs[k]);
        }
      }
    }
    if (children) {
      (Array.isArray(children) ? children : [children]).forEach(function (c) {
        if (c === null || c === undefined || c === false) return;
        e.appendChild(c.nodeType ? c : document.createTextNode(String(c)));
      });
    }
    return e;
  }

  function formatMoney(amount, currency) {
    const n = parseFloat(amount);
    if (!isFinite(n)) return "";
    const sym = currency === "USD" ? "$" : "";
    return sym + n.toFixed(2) + (sym ? " " + currency : "");
  }

  function formatDate(iso, withYear) {
    if (!iso) return "";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    var opts = { month: "short", day: "numeric" };
    if (withYear) opts.year = "numeric";
    return d.toLocaleDateString(undefined, opts);
  }

  function statusLabel(status) {
    switch (status) {
      case "cancelled": return "Cancelled";
      case "fulfilled": return "Fulfilled";
      case "partial":   return "Partially fulfilled";
      case "confirmed": return "Confirmed";
      case "refunded":  return "Refunded";
      case "pending":   return "Pending";
      default:          return status.charAt(0).toUpperCase() + status.slice(1);
    }
  }

  function statusIcon(status) {
    // Minimal inline SVGs matching the Shopify account UI: check for
    // confirmed/fulfilled, slashed-circle for cancelled, circle for other.
    const common = 'width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"';
    if (status === "fulfilled" || status === "confirmed") {
      return '<svg ' + common + '><path d="M3 8.5L6.5 12L13 4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    }
    if (status === "cancelled" || status === "refunded") {
      return '<svg ' + common + '><circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/><path d="M4 12L12 4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>';
    }
    return '<svg ' + common + '><circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/></svg>';
  }

  function passesFilter(order) {
    if (state.filter === "all") return true;
    if (state.filter === "open") {
      return order.status !== "cancelled" && order.status !== "fulfilled" && order.status !== "refunded";
    }
    if (state.filter === "fulfilled") return order.status === "fulfilled";
    if (state.filter === "cancelled") return order.status === "cancelled" || order.status === "refunded";
    return true;
  }

  function sortedOrders() {
    const out = state.orders.filter(passesFilter);
    out.sort(function (a, b) {
      const ta = new Date(a.created_at).getTime() || 0;
      const tb = new Date(b.created_at).getTime() || 0;
      return state.sort === "oldest" ? ta - tb : tb - ta;
    });
    return out;
  }

  // -----------------------------------------------------------------
  // API
  // -----------------------------------------------------------------
  async function fetchOrders() {
    state.loading = true;
    state.error = "";
    render();
    try {
      const resp = await fetch(PROXY_BASE + "/orders?status=any", {
        method: "GET",
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
      });
      const data = await resp.json().catch(function () { return {}; });
      if (!resp.ok || !data.ok) {
        throw new Error(data.message || ("Failed to load orders (" + resp.status + ")"));
      }
      state.orders = data.orders || [];
    } catch (e) {
      state.error = e.message || "Failed to load orders";
    } finally {
      state.loading = false;
      render();
    }
  }

  // -----------------------------------------------------------------
  // Buy Again
  // -----------------------------------------------------------------

  /**
   * Re-order every line item in an order. Custom-formula lines go through
   * /apps/formulas/add-to-cart (which creates a priced one-off variant);
   * everything else goes straight through /cart/add.js. We queue them one
   * at a time so Shopify's cart API doesn't drop items under concurrency.
   */
  async function buyAgain(order, btnEl) {
    state.buyingOrderId = order.id;
    const originalText = btnEl.textContent;
    btnEl.disabled = true;
    btnEl.textContent = "Adding...";
    render(); // lock all other buttons on this order

    let added = 0;
    const skipped = [];
    try {
      for (const li of order.line_items) {
        try {
          if (li.custom_formula && li.formula) {
            await buyAgainCustomLine(li);
          } else {
            await buyAgainStandardLine(li);
          }
          added++;
        } catch (lineErr) {
          // Promo credits (FREE INFINIT Bucks), gifts, and any product with no
          // current variant (e.g. an imported preset not yet mapped) can't be
          // re-added. Skip that single line and keep going so the rest of the
          // order still reaches the cart, instead of failing the whole reorder
          // (TW #36030424, Jason's request).
          console.warn("Buy Again: skipping line", li.title, lineErr && lineErr.message);
          skipped.push(li.title || "item");
        }
      }

      if (added === 0) {
        // Nothing in this order could be re-ordered (e.g. promo-only order).
        alert("Sorry, none of the items in this order can be re-ordered online.");
        btnEl.disabled = false;
        btnEl.textContent = originalText;
        state.buyingOrderId = null;
        render();
        return;
      }
      window.location.href = "/cart";
    } catch (e) {
      alert("Re-order failed: " + (e.message || "unknown error"));
      btnEl.disabled = false;
      btnEl.textContent = originalText;
      state.buyingOrderId = null;
      render();
    }
  }

  /**
   * For a custom-formula line item: ask the calculator API to create the
   * priced variant (mirrors the saved-formulas "Order" modal submit), then
   * add that variant to the cart with the original line-item properties.
   */
  async function buyAgainCustomLine(li) {
    // Always reconstruct the formula from scratch via the master custom-formula
    // base variant — the historical temp variant may have been deleted, and
    // reconstruction gives us today's pricing automatically. orders.php
    // supplies `fallback_base_variant_id` chosen by package size match.
    const baseVariantId = li.fallback_base_variant_id || li.variant_id || "";
    if (!baseVariantId) {
      throw new Error("Missing base_variant_id");
    }
    const payload = Object.assign({}, li.formula, {
      base_variant_id: baseVariantId,
      package_size: li.formula.package_size || extractNumeric(li.variant_title) || "25",
      quantity: li.quantity || 1,
    });

    const body = new FormData();
    body.set("payload_json", JSON.stringify(payload));

    const resp = await fetch(PROXY_BASE + "/add-to-cart", {
      method: "POST",
      body: body,
      credentials: "same-origin",
      headers: { "Accept": "application/json" },
    });
    const result = await resp.json().catch(function () { return {}; });
    if (!resp.ok || !result.ok || !result.variant_id) {
      throw new Error(result.message || "Could not build custom formula variant");
    }

    const cartItem = {
      id: parseInt(result.variant_id, 10),
      quantity: parseInt(result.quantity, 10) || li.quantity || 1,
      properties: result.properties || {},
    };
    if (result.selling_plan) {
      cartItem.selling_plan = parseInt(result.selling_plan, 10);
    }

    // Newly created one-off variants can take a moment to propagate through
    // Shopify's inventory/search index; retry on transient failures (same
    // pattern the saved-formulas Order modal uses).
    let cartResp;
    for (let attempt = 0; attempt < 5; attempt++) {
      if (attempt > 0) await new Promise(function (r) { setTimeout(r, 2000); });
      cartResp = await fetch("/cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: [cartItem] }),
      });
      if (cartResp.ok) return;
    }
    const err = await cartResp.json().catch(function () { return {}; });
    throw new Error(err.description || err.message || "Failed to add custom formula to cart");
  }

  async function buyAgainStandardLine(li) {
    // Prefer the line's own variant; fall back to the mapped current variant
    // that orders.php resolves for imported preset lines (TW #36030424 #8).
    const variantId = li.variant_id || li.fallback_base_variant_id || "";
    if (!variantId) {
      throw new Error("Missing variant for " + (li.title || "item"));
    }
    const cartItem = {
      id: parseInt(variantId, 10),
      quantity: li.quantity || 1,
    };
    if (li.properties && Object.keys(li.properties).length) {
      // Strip private (_-prefixed) properties that would confuse a fresh
      // cart add. Public ones (notes, gift messages, etc.) round-trip fine.
      const cleaned = {};
      for (const k in li.properties) {
        if (!k.startsWith("_")) cleaned[k] = li.properties[k];
      }
      if (Object.keys(cleaned).length) cartItem.properties = cleaned;
    }
    const resp = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: [cartItem] }),
    });
    if (!resp.ok) {
      const err = await resp.json().catch(function () { return {}; });
      throw new Error(err.description || err.message || "Failed to add " + (li.title || "item"));
    }
  }

  function extractNumeric(str) {
    if (!str) return null;
    const m = String(str).match(/(\d+)/);
    return m ? m[1] : null;
  }

  // -----------------------------------------------------------------
  // Rendering
  // -----------------------------------------------------------------
  function renderHeader() {
    const title = el("h1", {
      style: {
        fontSize: "24px",
        fontWeight: "700",
        color: "#000",
        lineHeight: "1.25",
        margin: "0",
        flex: "1",
        fontFamily: "Inter,-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif",
        textTransform: "none",
        letterSpacing: "normal",
      },
    }, ["My Orders"]);

    const filterWrap = el("div", {
      class: "my-orders__filters",
      style: { display: "flex", gap: "12px", alignItems: "center" },
    }, [
      buildSelect("Filter", "filter", state.filter, [
        { value: "all",        label: "All orders" },
        { value: "open",       label: "Open" },
        { value: "fulfilled",  label: "Fulfilled" },
        { value: "cancelled",  label: "Cancelled" },
      ], function (v) { state.filter = v; render(); }),
      buildSelect("Sort", "sort", state.sort, [
        { value: "newest", label: "Newest first" },
        { value: "oldest", label: "Oldest first" },
      ], function (v) { state.sort = v; render(); }),
    ]);

    return el("div", {
      class: "my-orders__header",
      style: {
        display: "flex", alignItems: "center", justifyContent: "space-between",
        gap: "16px", flexWrap: "wrap", marginBottom: "24px",
      },
    }, [title, filterWrap]);
  }

  function buildSelect(ariaLabel, name, current, options, onChange) {
    const sel = el("select", {
      "aria-label": ariaLabel,
      name: name,
      style: {
        padding: "8px 28px 8px 12px",
        border: "1px solid #dcdcdc",
        borderRadius: "8px",
        background: "#fff",
        fontSize: "14px",
        cursor: "pointer",
        minWidth: "140px",
      },
      onchange: function (ev) { onChange(ev.target.value); },
    }, options.map(function (o) {
      const opt = el("option", { value: o.value }, [o.label]);
      if (o.value === current) opt.selected = true;
      return opt;
    }));
    return sel;
  }

  function renderCard(order, isPrevious) {
    const otherOrderBuying = state.buyingOrderId && state.buyingOrderId !== order.id;
    const thisBuying = state.buyingOrderId === order.id;
    // Subscription orders lose their custom-formula context on the renewal
    // line items (Shopify Subscriptions strips the line-item properties), so
    // Buy Again would re-add bare variants with no formula details. Block
    // Buy Again on these orders and tell the customer to use the Subscriptions
    // portal instead. Magento 1 imported orders also can't be reordered (no
    // resolvable variants), so Buy Again is disabled for them (TW #36030424 #9).
    const isSubscriptionOrder = !!order.has_subscription;
    const isM1 = !!order.is_m1;
    const disabled = otherOrderBuying || isSubscriptionOrder || isM1;
    // Original Magento order date (backdated into processed_at on import).
    const orderDate = order.processed_at || order.created_at;

    // "My Previous Orders" (imported) cards omit the status badge: Shopify won't
    // let us backdate the fulfilled date to the Magento date, so the status/date
    // shown would be the import date and confuse customers (TW #36030424 #3).
    const statusRow = isPrevious ? null : el("div", {
      style: {
        display: "flex", alignItems: "center", gap: "8px",
        padding: "14px 18px", background: "#f6f6f7", borderBottom: "1px solid #ececec",
        color: order.status === "cancelled" || order.status === "refunded" ? "#bf0711" : "#111",
      },
    }, [
      el("span", { html: statusIcon(order.status), style: { display: "inline-flex" } }),
      el("div", { style: { display: "flex", flexDirection: "column", lineHeight: "1.2" } }, [
        el("span", { style: { fontWeight: "600", fontSize: "14px" } }, [statusLabel(order.status)]),
        el("span", { style: { fontSize: "12px", color: "#555" } }, [formatDate(orderDate)]),
      ]),
    ]);

    const thumbs = order.line_items.slice(0, 4).map(function (li) {
      if (li.image) {
        return el("div", {
          style: {
            aspectRatio: "1 / 1",
            background: "#f4f4f4 center/cover no-repeat url(" + JSON.stringify(li.image).slice(1, -1) + ")",
            borderRadius: "4px",
          },
          title: li.title,
        });
      }
      return el("div", {
        style: {
          aspectRatio: "1 / 1", background: "#f4f4f4", borderRadius: "4px",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#888", fontSize: "11px", padding: "8px", textAlign: "center",
        },
        title: li.title,
      }, [li.title.length > 40 ? li.title.slice(0, 40) + "..." : li.title]);
    });

    // Pad to 4 cells so grid stays visually balanced
    while (thumbs.length < Math.min(4, Math.max(1, order.line_items.length))) {
      thumbs.push(el("div", { style: { aspectRatio: "1 / 1" } }));
    }

    const thumbGrid = el("div", {
      style: {
        display: "grid",
        gridTemplateColumns: order.line_items.length <= 1 ? "1fr" : "1fr 1fr",
        gap: "8px",
        padding: "18px",
      },
    }, thumbs);

    // Previous (imported) orders show the original Magento order number instead
    // of the Shopify order number (TW #36030424 #4) — that's the reference the
    // customer recognizes — plus the original order date (since the status row
    // that normally carries the date is hidden for these).
    const orderLineText = isPrevious
      ? ("Original order #" + (order.magento_increment_id || (order.name || "").replace(/^#/, "")))
      : ("Order " + (order.name || "#" + order.order_number));
    const metaChildren = [
      el("div", { style: { fontSize: "15px", color: "#111" } }, [
        order.item_count + " " + (order.item_count === 1 ? "item" : "items"),
      ]),
      el("div", { style: { fontSize: "13px", color: "#666" } }, [orderLineText]),
    ];
    if (isPrevious) {
      metaChildren.push(el("div", { style: { fontSize: "12px", color: "#888" } }, ["Ordered " + formatDate(orderDate, true)]));
    }
    metaChildren.push(el("div", { style: { fontSize: "15px", color: "#111", fontWeight: "600", marginTop: "6px" } }, [
      formatMoney(order.total_price, order.currency),
    ]));
    const meta = el("div", {
      style: { padding: "0 18px 16px", display: "flex", flexDirection: "column", gap: "2px" },
    }, metaChildren);

    let buttonLabel = "Buy again";
    let buttonTitle = "";
    if (thisBuying) {
      buttonLabel = "Adding...";
    } else if (isSubscriptionOrder) {
      buttonLabel = "Manage in Subscriptions";
      buttonTitle = "This is a subscription order. Manage your renewals in the Subscriptions section of your account.";
    } else if (isM1) {
      buttonTitle = "This order is from our previous store and can't be reordered online.";
    }

    const btn = el("button", {
      type: "button",
      class: "my-orders__buy-again",
      disabled: disabled || thisBuying ? "disabled" : null,
      title: buttonTitle,
      style: {
        width: "calc(100% - 36px)", margin: "0 18px 18px",
        padding: "12px 16px", background: "#fff", color: "#111",
        border: "1px solid #111", borderRadius: "6px",
        fontSize: "14px", fontWeight: "600", cursor: disabled || thisBuying ? "not-allowed" : "pointer",
        opacity: disabled ? "0.5" : "1", transition: "background 0.15s ease",
      },
      onmouseover: function (ev) { if (!ev.currentTarget.disabled) ev.currentTarget.style.background = "#f6f6f7"; },
      onmouseout:  function (ev) { ev.currentTarget.style.background = "#fff"; },
      onclick: function (ev) {
        if (isSubscriptionOrder || isM1) return;
        buyAgain(order, ev.currentTarget);
      },
    }, [buttonLabel]);

    return el("div", {
      class: "my-orders__card",
      style: {
        background: "#fff", border: "1px solid #e5e5e5", borderRadius: "12px",
        overflow: "hidden", display: "flex", flexDirection: "column",
      },
    }, [statusRow, thumbGrid, meta, btn].filter(Boolean));
  }

  function renderGrid() {
    const orders = sortedOrders();
    if (!orders.length) {
      return el("div", {
        style: {
          padding: "48px 16px", textAlign: "center",
          background: "#fafafa", border: "1px dashed #ddd", borderRadius: "12px",
          color: "#555",
        },
      }, [
        el("p", { style: { margin: "0 0 8px", fontSize: "16px", fontWeight: "600", color: "#111" } }, [
          state.orders.length ? "No orders match this filter." : "You don't have any orders yet.",
        ]),
        el("p", { style: { margin: "0", fontSize: "14px" } }, [
          state.orders.length ? "" : "When you place an order, it will appear here.",
        ]),
      ]);
    }

    // Two sections (TW #36030424 #1): native Shopify orders under "My Orders",
    // imported Magento orders under "My Previous Orders", split by the imported
    // flag and separated by a divider.
    const native   = orders.filter(function (o) { return !o.imported; });
    const previous = orders.filter(function (o) { return o.imported; });

    function gridFor(list, isPrevious) {
      return el("div", {
        class: "my-orders__grid",
        style: {
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
          gap: "24px",
        },
      }, list.map(function (o) { return renderCard(o, isPrevious); }));
    }
    function sectionTitle(text) {
      return el("h2", {
        style: {
          margin: "0 0 16px", fontSize: "18px", fontWeight: "700",
          letterSpacing: "0.04em", textTransform: "uppercase", color: "#111",
        },
      }, [text]);
    }

    const wrap = el("div", {});
    if (native.length) {
      wrap.appendChild(sectionTitle("My Orders"));
      wrap.appendChild(gridFor(native, false));
    }
    if (previous.length) {
      if (native.length) {
        wrap.appendChild(el("hr", { style: { border: "none", borderTop: "2px solid #e5e5e5", margin: "40px 0 28px" } }));
      }
      wrap.appendChild(sectionTitle("My Previous Orders"));
      wrap.appendChild(gridFor(previous, true));
    }
    return wrap;
  }

  function render() {
    root.innerHTML = "";
    root.appendChild(renderHeader());

    if (state.loading) {
      root.appendChild(el("div", {
        style: { padding: "48px 16px", textAlign: "center", color: "#666" },
      }, ["Loading orders..."]));
      return;
    }

    if (state.error) {
      root.appendChild(el("div", {
        style: {
          padding: "24px", background: "#fff5f5", border: "1px solid #f2c2c2",
          borderRadius: "8px", color: "#a40000",
        },
      }, [state.error]));
      return;
    }

    root.appendChild(renderGrid());
  }

  // -----------------------------------------------------------------
  // Boot
  // -----------------------------------------------------------------
  render();
  fetchOrders();
})();
